import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useAuth } from "@/features/auth/hooks/useAuthentication";
import { getAuthLoginUrl } from "@/features/auth/lib/app-origin";
import { ChatProvider } from "@/features/chat/context/ChatContext";
import { ChatStreamProvider } from "@/features/chat/stream/ChatStreamProvider";
import { GlobalSettingsModal } from "@/shared/components/GlobalSettingsModal";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { ChatWidget } from "@/widgets/chat";
import { Sidebar, Topbar } from "@/widgets/navigation";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

const REDIRECT_COUNTDOWN_SECONDS = 5;
const LOGIN_REDIRECT_AT_KEY = "veritab.loginRedirectAt";
const LOGIN_REDIRECT_COOLDOWN_MS = 15_000;

function AuthErrorPage({ error }: { error: Error }) {
  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS);

  const redirectToLogin = () => {
    window.location.href = getAuthLoginUrl();
  };

  useEffect(() => {
    if (countdown <= 0) {
      redirectToLogin();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  return (
    <div className="flex h-screen bg-background items-center justify-center">
      <div className="text-center space-y-4 max-w-sm">
        <h2 className="text-lg font-semibold">Session expired</h2>
        <p className="text-sm text-muted-foreground">
          {error.message}
        </p>
        <p className="text-xs text-muted-foreground">
          Redirecting to login in {countdown}s...
        </p>
        <Button onClick={redirectToLogin} variant="outline" size="sm">
          Return to login now
        </Button>
      </div>
    </div>
  );
}

function AuthenticatedLayout() {
  const auth = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loginThrottled, setLoginThrottled] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isAgentRoute = pathname.startsWith("/agent");

  useEffect(() => {
    if (auth.isAuthenticated) {
      sessionStorage.removeItem(LOGIN_REDIRECT_AT_KEY);
      return;
    }
    if (auth.isLoading) return;

    const lastAttempt = Number(sessionStorage.getItem(LOGIN_REDIRECT_AT_KEY) ?? 0);
    if (Date.now() - lastAttempt < LOGIN_REDIRECT_COOLDOWN_MS) {
      setLoginThrottled(true);
      return;
    }

    sessionStorage.setItem(LOGIN_REDIRECT_AT_KEY, String(Date.now()));
    window.location.href = getAuthLoginUrl();
  }, [auth.isLoading, auth.isAuthenticated]);

  // Open sidebar by default on larger screens
  useEffect(() => {
    if (window.innerWidth >= 768) {
      setSidebarOpen(true);
    }
  }, []);

  // Show loading state while authentication is being checked
  if (auth.isLoading) {
    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    if (loginThrottled) {
      return (
        <div className="flex h-screen bg-background items-center justify-center">
          <div className="text-center space-y-2 max-w-sm">
            <h2 className="text-lg font-semibold">Login paused</h2>
            <p className="text-sm text-muted-foreground">
              Too many login redirects. Wait a minute, then try again.
            </p>
            <Button onClick={() => {
              sessionStorage.removeItem(LOGIN_REDIRECT_AT_KEY);
              window.location.href = getAuthLoginUrl();
            }} variant="outline" size="sm">
              Try login again
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Handle other authentication errors
  if (auth.error) {
    return <AuthErrorPage error={auth.error} />;
  }

  return (
    <ChatProvider>
      <ChatStreamProvider />
      <div className="flex h-screen bg-background">
        {/* Mobile overlay backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onOpen={() => setSidebarOpen(true)}
        />

        {/* Main content area */}
        <div className={cn("flex-1 flex flex-col overflow-hidden md:min-w-[390px]")}>
          {/* Topbar */}
          <Topbar onMenuClick={() => setSidebarOpen(true)} sidebarOpen={sidebarOpen} />

          {/* Page content */}
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-background">
            <Outlet />
          </main>
        </div>

        {/* Chat widget (contains org memory pane). Hidden on /agent — that page renders its own. */}
        {!isAgentRoute && <ChatWidget />}

        {/* Global UI Components */}
        <GlobalSettingsModal />
      </div>
    </ChatProvider>
  );
}
